import React, { useEffect, useState } from "react";
import Layout from "../../../components/layout/Layout";
import FilterableTabCard from "../../../components/tabs/FilterableTabCard";
import CustomDrawer from "../../../components/layout/CustomDrawer";
import FilterPanel from "../../../components/filters/FilterPanel";
import { CorporateAccessEvents, TouchPoints, PlanSummary } from "./tabs";
import CommonNavigator from "../../../components/common/CommonNavigator";
import { useGetAnalystListQuery } from "../../../redux/service/modules/dashboard";
import { useSelector } from "react-redux";
import { useGetCorporateAccessGroupsQuery } from "../../../redux/service/modules/dashboard";
import { useLazyGetLazyCorporatePlansSummaryQuery } from "../../../redux/service/modules/dashboard/lazzyQuery";
import { Trend } from "./tabs/Trend";
import { Historical } from "./tabs/Historical";
import Top25 from "./tabs/Top25";
import { useFetchUserQuery } from "../../../redux/service/modules/auth";
import { useSearchParams } from "react-router-dom";
import { handleExport } from "../../../redux/service/modules/exportToExcel";
import { convertToDates } from "../../../utils/commonUtils";
import { useGetUserListQuery } from "../../../redux/service/modules/sales";

const createTabs = (user) => {
    if (user?.role === "hybrid" || user?.role === "admin") {
        return [
            {
                id: "trend",
                label: "Trend",
                content: Trend,
            },
            {
                id: "top-25-clients",
                label: "Top 25 Clients",
                content: Top25,
            },
            {
                id: "events-meetings",
                label: "Events & Meetings",
                content: CorporateAccessEvents,
                showFilter: true,
            },
            {
                id: "plan-summary",
                label: "Plan & Summary",
                content: PlanSummary,
                showFilter: true,
            },
            {
                id: "touch-points",
                label: "Touch Points",
                content: TouchPoints,
            },
            {
                id: "historicals",
                label: "Historicals",
                content: Historical,
            },
        ];
    } else if (user?.role === "sales" || user?.role === "head_of_sales") {
        return [
            {
                id: "trend",
                label: "Trend",
                content: Trend,
            },
            {
                id: "top-25-clients",
                label: "Top 25 Clients",
                content: Top25,
                showFilter: true,
            },
            {
                id: "events-meetings",
                label: "Events & Meetings",
                content: CorporateAccessEvents,
                showFilter: true,
            },
            {
                id: "plan-summary",
                label: "Plan & Summary",
                content: PlanSummary,
                showFilter: true,
            },
            {
                id: "historicals",
                label: "Historicals",
                content: Historical,
            },
        ];
    } else if (user?.role === "analyst" || user?.role === "head_of_research") {
        return [
            {
                id: "events-meetings",
                label: "Events & Meetings",
                content: CorporateAccessEvents,
                showFilter: true,
            },
            {
                id: "plan-summary",
                label: "Plan & Summary",
                content: PlanSummary,
                showFilter: true,
            },
            {
                id: "touch-points",
                label: "Touch Points",
                content: TouchPoints,
            },
        ];
    }
    return [];
    // };
}

const CorporateAccess = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFilterOpen, toggleFilter] = useState(false);
    const { data: user } = useFetchUserQuery();
    const dateRange = useSelector((state) => state.date);
    const [requiredFilters, setRequiredFilters] = useState([]);
    const [filters, setFilters] = useState([]);
    const [filterDetails, setFilterDetails] = useState({
        appliedFilters: {
            initialDateRange: dateRange,
            analyst: [],
            group: [],
        },
        filterCount: 0,
        rawFilters: {},
    });

    // const { data: analystList } = useGetAnalystListQuery();
    const { data: corporateAccessGroups } = useGetCorporateAccessGroupsQuery();
    const { data: userList } = useGetUserListQuery();

    const [planSummaryQuery] = useLazyGetLazyCorporatePlansSummaryQuery();

    const exportModule = {
        "plan-summary": planSummaryQuery,
    };
    const availableFiltersMap = {
        analyst: {
            name: "analystName",
            label: "Analyst Name",
            key: "analyst",
            options:
                userList?.map((analyst) => ({
                    label: analyst.username,
                    id: analyst.id,
                })) || [],
            multiSelect: true,
        },
        group: {
            name: "groupName",
            label: "Group Name",
            key: "group",
            options:
                corporateAccessGroups?.groups?.map((group) => ({
                    label: group,
                    id: group,
                })) || [],
            multiSelect: true,
        },
    };

    useEffect(() => {
        updateFiltersBasedOnRequired();
    }, [requiredFilters, corporateAccessGroups, userList]);

    const updateFiltersBasedOnRequired = () => {
        const activeFilters = requiredFilters
            .map((filterKey) => availableFiltersMap[filterKey])
            .filter(Boolean); // Remove any undefined filters

        setFilters(activeFilters);
    };

    const onFilterApply = (transformedFilters, filterCount, rawFilters) => {
        toggleFilter(false);
        setFilterDetails({
            appliedFilters: {
                ...transformedFilters,
                initialDateRange: dateRange,
            },
            filterCount,
            rawFilters,
        });
    };

    const tabs = createTabs(user);
    const defaultTabId = tabs?.[0]?.id || "trend";
    const currentTabId = searchParams.get("tab") || defaultTabId;
    const [activeTab, setActiveTab] = useState(
        tabs?.findIndex((tab) => tab.id === currentTabId)
    );

    useEffect(() => {
        const tabIndex = tabs?.findIndex((tab) => tab.id === currentTabId);
        setActiveTab(tabIndex >= 0 ? tabIndex : 0);
        if (!searchParams.get("tab")) {
            setSearchParams({ tab: defaultTabId });
        }
    }, [currentTabId, tabs, setSearchParams, defaultTabId]);

    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        setSearchParams({ tab: tabs[newValue].id });
        setFilterDetails({
            appliedFilters: {
                initialDateRange: dateRange,
                analyst: [],
                group: [],
            },
            filterCount: 0,
            rawFilters: {},
        });
    };

    const triggerExportToExcel = () => {
        handleExport(exportModule[tabs[activeTab].id], {
            appliedFilters: filterDetails.appliedFilters,
            ...(convertToDates(dateRange) || {}),
        });
    };

    return (
        <Layout>
            <div>
                <CommonNavigator
                    title={"Corporate Access"}
                    showExportToExcel={tabs[activeTab]?.showExportToExcel}
                    triggerExportToExcel={triggerExportToExcel}
                />
                <div className="mt-2">
                    <FilterableTabCard
                        tabs={tabs}
                        onTabClick={handleTabChange}
                        onFilterClick={() => toggleFilter(true)}
                        appliedFilters={filterDetails?.appliedFilters}
                        filterCount={filterDetails?.filterCount}
                        setRequiredFilters={setRequiredFilters}
                        value={activeTab}
                    />
                    <CustomDrawer open={isFilterOpen}>
                        <FilterPanel
                            key={`filter-panel-${activeTab}`}
                            filtersContent={filters}
                            handlePanelClose={() => toggleFilter(false)}
                            onApply={onFilterApply}
                            appliedFilters={filterDetails.rawFilters}
                        />
                    </CustomDrawer>
                </div>
            </div>
        </Layout>
    );
};

export default CorporateAccess;
